import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaExternalLinkAlt, FaBug, FaCheckCircle } from 'react-icons/fa';
import './Projects.css';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [showNotification, setShowNotification] = useState(false);

  const handleViewReports = () => {
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  };

  const projects = [
    {
      id: 1,
      title: 'Feel Pro App',
      type: 'Mobile Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Manual Testing', 'Postman', 'Jira', 'Android Studio', 'Xcode'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Quality assurance testing for Feel Pro mobile application ensuring seamless user experience and functionality across iOS and Android platforms.',
      scope: [
        'User interface and navigation testing',
        'Feature functionality validation',
        'Cross-platform compatibility testing',
        'API integration testing',
        'Performance and usability testing'
      ],
      testCases: 280,
      defects: { critical: 4, high: 12, medium: 25, low: 15 },
      artifacts: ['Test Plan', 'Test Cases', 'Bug Reports', 'Test Summary'],
      githubLink: 'https://github.com',
      highlights: [
        'Ensured consistent user experience across platforms',
        'Identified and resolved critical bugs before release',
        'Improved app stability through comprehensive testing'
      ]
    },
    {
      id: 2,
      title: 'Amal App',
      type: 'Mobile Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Manual Testing', 'Postman', 'Jira', 'Firebase'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Comprehensive testing of Amal mobile application focusing on functionality, performance, and user experience optimization.',
      scope: [
        'End-to-end feature testing',
        'User workflow validation',
        'API endpoint testing',
        'Device compatibility testing',
        'Regression testing for updates'
      ],
      testCases: 320,
      defects: { critical: 5, high: 14, medium: 28, low: 18 },
      artifacts: ['Test Strategy', 'Test Cases', 'Bug Reports', 'RTM'],
      githubLink: 'https://github.com',
      highlights: [
        'Validated core functionality across multiple devices',
        'Improved app performance through defect identification',
        'Ensured smooth user experience'
      ]
    },
    {
      id: 3,
      title: 'Mashoor App',
      type: 'Mobile Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Manual Testing', 'Postman', 'Jira', 'Charles Proxy'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Quality assurance for Mashoor mobile application with focus on feature validation, API testing, and ensuring optimal user experience.',
      scope: [
        'Feature functionality testing',
        'API integration validation',
        'User interface testing',
        'Cross-device compatibility',
        'Performance testing'
      ],
      testCases: 295,
      defects: { critical: 3, high: 11, medium: 24, low: 16 },
      artifacts: ['Test Plan', 'Test Cases', 'Defect Reports', 'Test Summary'],
      githubLink: 'https://github.com',
      highlights: [
        'Ensured reliable API integrations',
        'Validated user workflows across platforms',
        'Maintained high quality standards'
      ]
    },
    {
      id: 4,
      title: 'Kids Diary App',
      type: 'Mobile Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Manual Testing', 'Postman', 'Jira', 'TestFlight'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Testing of Kids Diary mobile application ensuring child-friendly interface, data security, and seamless functionality.',
      scope: [
        'Child-safe content validation',
        'User interface and usability testing',
        'Data security testing',
        'Feature functionality validation',
        'Parent control features testing'
      ],
      testCases: 265,
      defects: { critical: 2, high: 9, medium: 22, low: 14 },
      artifacts: ['Test Cases', 'Bug Reports', 'Security Test Report', 'RTM'],
      githubLink: 'https://github.com',
      highlights: [
        'Ensured child-safe user experience',
        'Validated data privacy and security',
        'Improved app usability for target audience'
      ]
    },
    {
      id: 5,
      title: 'AI_SDLC SaaS Platform',
      type: 'SaaS Platform',
      role: 'Software Quality Assurance Engineer',
      tools: ['Selenium', 'Postman', 'JMeter', 'Jira', 'Git'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'API Testing', 'Performance Testing'],
      overview: 'Comprehensive quality assurance for AI_SDLC SaaS platform including web application testing, API validation, and performance optimization.',
      scope: [
        'Web application functionality testing',
        'API endpoint validation',
        'Performance and load testing',
        'User authentication and authorization',
        'Integration testing'
      ],
      testCases: 420,
      defects: { critical: 6, high: 16, medium: 32, low: 20 },
      artifacts: ['Test Plan', 'Test Cases', 'API Documentation', 'Performance Reports', 'Test Summary'],
      githubLink: 'https://github.com',
      highlights: [
        'Validated complex SaaS functionalities',
        'Ensured platform scalability and performance',
        'Comprehensive API testing coverage'
      ]
    },
    {
      id: 6,
      title: 'MVP 100 Hours',
      type: 'Web Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Selenium', 'Postman', 'Jira', 'Browser DevTools'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Quality assurance testing for MVP 100 Hours web application ensuring functionality, usability, and cross-browser compatibility.',
      scope: [
        'Web application functionality testing',
        'Cross-browser compatibility testing',
        'Responsive design validation',
        'API integration testing',
        'User workflow validation'
      ],
      testCases: 310,
      defects: { critical: 5, high: 13, medium: 27, low: 17 },
      artifacts: ['Test Plan', 'Test Cases', 'Bug Reports', 'Browser Compatibility Report'],
      githubLink: 'https://github.com',
      highlights: [
        'Ensured cross-browser compatibility',
        'Validated responsive design across devices',
        'Improved overall application quality'
      ]
    },
    {
      id: 7,
      title: 'ThinkBusinessOnly',
      type: 'Web Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Selenium', 'Postman', 'Jira', 'Git'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Testing of ThinkBusinessOnly web application with focus on business workflows, functionality validation, and user experience.',
      scope: [
        'Business workflow testing',
        'Feature functionality validation',
        'API integration testing',
        'User interface testing',
        'Data validation testing'
      ],
      testCases: 340,
      defects: { critical: 4, high: 14, medium: 29, low: 19 },
      artifacts: ['Test Strategy', 'Test Cases', 'Defect Reports', 'RTM'],
      githubLink: 'https://github.com',
      highlights: [
        'Validated critical business workflows',
        'Ensured data integrity and accuracy',
        'Improved user experience through testing'
      ]
    },
    {
      id: 8,
      title: 'DOT App',
      type: 'Web Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Selenium', 'Postman', 'Jira', 'Chrome DevTools'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Quality assurance for DOT web application ensuring robust functionality, API reliability, and optimal user experience.',
      scope: [
        'Web application testing',
        'API endpoint validation',
        'User interface testing',
        'Functionality validation',
        'Cross-browser testing'
      ],
      testCases: 285,
      defects: { critical: 3, high: 10, medium: 23, low: 15 },
      artifacts: ['Test Plan', 'Test Cases', 'Bug Reports', 'Test Summary'],
      githubLink: 'https://github.com',
      highlights: [
        'Ensured reliable API integrations',
        'Validated user workflows',
        'Maintained quality standards'
      ]
    },
    {
      id: 9,
      title: 'Guess Chat App',
      type: 'Mobile Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Manual Testing', 'Postman', 'Jira', 'Firebase'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing', 'API Testing'],
      overview: 'Testing of Guess Chat mobile application focusing on messaging functionality, real-time communication, and user experience.',
      scope: [
        'Messaging functionality testing',
        'Real-time communication validation',
        'User interface testing',
        'API integration testing',
        'Performance testing'
      ],
      testCases: 270,
      defects: { critical: 4, high: 11, medium: 24, low: 16 },
      artifacts: ['Test Cases', 'Bug Reports', 'API Test Report', 'Test Summary'],
      githubLink: 'https://github.com',
      highlights: [
        'Validated real-time messaging features',
        'Ensured smooth user communication',
        'Improved app reliability'
      ]
    },
    {
      id: 10,
      title: 'Auto Silent App',
      type: 'Mobile Application',
      role: 'Software Quality Assurance Engineer',
      tools: ['Manual Testing', 'Jira', 'Android Studio'],
      testingTypes: ['Manual Testing', 'Regression Testing', 'Smoke & Sanity Testing'],
      overview: 'Quality assurance for Auto Silent mobile application ensuring automation features, system integration, and user-friendly functionality.',
      scope: [
        'Automation feature testing',
        'System integration validation',
        'User interface testing',
        'Device compatibility testing',
        'Feature functionality validation'
      ],
      testCases: 220,
      defects: { critical: 2, high: 8, medium: 18, low: 12 },
      artifacts: ['Test Plan', 'Test Cases', 'Bug Reports', 'Device Compatibility Report'],
      githubLink: 'https://github.com',
      highlights: [
        'Validated automation features',
        'Ensured system integration',
        'Improved user experience'
      ]
    }
  ];

  return (
    <section id="projects" className="projects">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">QA Projects</h2>
          <p className="section-subtitle">
            Real-world testing projects demonstrating comprehensive QA expertise
          </p>
        </motion.div>

        <div className="projects-grid">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              className="project-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              onClick={() => setSelectedProject(project)}
            >
              <div className="project-header">
                <span className="project-type">{project.type}</span>
                <h3>{project.title}</h3>
                <p className="project-role">{project.role}</p>
              </div>

              <div className="project-tools">
                {project.tools.map((tool, idx) => (
                  <span key={idx} className="tool-badge">{tool}</span>
                ))}
              </div>

              <div className="project-testing-types">
                {project.testingTypes.map((type, idx) => (
                  <span key={idx} className="testing-badge">{type}</span>
                ))}
              </div>

              <button className="view-details-btn">View Details →</button>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="and-more-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <p className="and-more-text">+ And More</p>
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <motion.div
            className="project-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              className="project-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-modal" onClick={() => setSelectedProject(null)}>
                <FaTimes />
              </button>

              <div className="modal-header">
                <span className="modal-type">{selectedProject.type}</span>
                <h2>{selectedProject.title}</h2>
                <p className="modal-role">{selectedProject.role}</p>
              </div>

              <div className="modal-content">
                <div className="modal-section">
                  <h3>Project Overview</h3>
                  <p>{selectedProject.overview}</p>
                </div>

                <div className="modal-section">
                  <h3>Testing Scope</h3>
                  <ul className="scope-list">
                    {selectedProject.scope.map((item, idx) => (
                      <li key={idx}>
                        <FaCheckCircle className="scope-icon" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="modal-stats">
                  <div className="stat-card">
                    <div className="stat-number">{selectedProject.testCases}</div>
                    <div className="stat-label">Test Cases</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">
                      {Object.values(selectedProject.defects).reduce((a, b) => a + b, 0)}
                    </div>
                    <div className="stat-label">Defects Found</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{selectedProject.tools.length}</div>
                    <div className="stat-label">Tools Used</div>
                  </div>
                </div>

                <div className="modal-section">
                  <h3>Defects by Severity</h3>
                  <div className="defects-grid">
                    <div className="defect-item critical">
                      <FaBug />
                      <span className="defect-count">{selectedProject.defects.critical}</span>
                      <span className="defect-label">Critical</span>
                    </div>
                    <div className="defect-item high">
                      <FaBug />
                      <span className="defect-count">{selectedProject.defects.high}</span>
                      <span className="defect-label">High</span>
                    </div>
                    <div className="defect-item medium">
                      <FaBug />
                      <span className="defect-count">{selectedProject.defects.medium}</span>
                      <span className="defect-label">Medium</span>
                    </div>
                    <div className="defect-item low">
                      <FaBug />
                      <span className="defect-count">{selectedProject.defects.low}</span>
                      <span className="defect-label">Low</span>
                    </div>
                  </div>
                </div>

                <div className="modal-section">
                  <h3>Key Highlights</h3>
                  <ul className="highlights-list">
                    {selectedProject.highlights.map((highlight, idx) => (
                      <li key={idx}>
                        <FaCheckCircle className="highlight-icon" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="modal-section">
                  <h3>Test Artifacts</h3>
                  <div className="artifacts-grid">
                    {selectedProject.artifacts.map((artifact, idx) => (
                      <span key={idx} className="artifact-badge">{artifact}</span>
                    ))}
                  </div>
                </div>

                <div className="modal-actions">
                  <button className="modal-btn secondary" onClick={handleViewReports}>
                    <FaExternalLinkAlt /> View Test Reports
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showNotification && (
          <motion.div
            className="notification-popup"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3 }}
          >
            <p>Contact for reports</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
